import { useQuery } from '@tanstack/react-query';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { ProjectExecutionExplorer } from '@/components/project-execution-explorer';
import { api } from '@/lib/api';

export default function ProjectExecutionsPage(): import('react/jsx-runtime').JSX.Element {
  const { id: projectId = '' } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    enabled: projectId.length > 0,
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  const project = data?.project;
  if (project?.id == null) return <Navigate to="/projects" replace />;

  return (
    <div className="mx-auto max-w-[1400px] w-full px-6 md:px-8 py-8 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3 mb-1">
            <Link
              to={`/projects/${projectId}`}
              className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {project.name}
            </Link>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Executions</h1>
          <p className="text-sm text-muted-foreground/60">
            Browse and investigate every run for{' '}
            <span className="font-mono text-foreground/80 px-1 bg-white/[0.03] rounded">
              {project.name}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 text-[9px] font-mono text-muted-foreground/30">
          <span>click filters — no query syntax needed</span>
        </div>
      </div>

      {/* Explorer */}
      <ProjectExecutionExplorer projectId={projectId} projectName={project.name ?? ''} />
    </div>
  );
}
